import { useEffect } from "react";

/**
 * useCanonical — bulletproof per-page canonical URL setter.
 *
 * Background: react-helmet (the version pinned in package.json) sometimes
 * fails to serialise dynamic `href={var}` values onto void elements like
 * <link>, resulting in `<link rel="canonical">` with no href. That breaks
 * Google's canonical signal and causes "Alternative page with proper
 * canonical" indexing issues.
 *
 * This hook bypasses Helmet for the canonical link tag and writes it
 * directly into <head>. It also cleans up the tag on unmount so SPA
 * navigation between pages doesn't leak stale canonical values.
 *
 * Usage:
 *   useCanonical("https://ezeefitofficesystems.in/some-page");
 */
export default function useCanonical(href) {
  useEffect(() => {
    if (!href) return undefined;
    let link = document.querySelector('link[rel="canonical"]');
    const created = !link;
    if (created) {
      link = document.createElement("link");
      link.setAttribute("rel", "canonical");
      document.head.appendChild(link);
    }
    link.setAttribute("href", href);

    return () => {
      // Remove canonical on route change so the NEXT route can set its own
      // (and we never have a stale canonical pointing to the previous page).
      if (link && link.parentNode) {
        link.parentNode.removeChild(link);
      }
    };
  }, [href]);
}
